import React, { useContext, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { apiGet } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { LoadingComponent } from '../components';
import { toast } from 'react-toastify';

export default function UnifiedDashboard() {
  const { userInfo } = useContext(UserContext)
  const [isLoading, setIsLoading] = useState(true)
  const [googleData, setGoogleData] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [linkedinData, setLinkedinData] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [platform, setPlatform] = useState('all');
  const navigate = useNavigate()

  const checkIfUserHasAccess = async () => {
    if(userInfo?.access){
      apiGet('user-info/').then(res => {
        // currently we are checking if we have trial enabled 
        if(res?.trial?.is_trial){
          setIsLoading(false)
          return
        }
        navigate('/')
      }).catch(err => {
        console.log(err.response && err.response.data.detail ? err.response.data.detail : 'failed to get user info')
        navigate('/')
      })
    }
  }

  useEffect(() => {
     const runAccessAndSync = async () => {
        try {
          await checkIfUserHasAccess()
          const [google, meta, linkedin] = await Promise.all([
            apiGet('auth/google/performance/'),
            apiGet('auth/meta/performance/'),
            apiGet('auth/linkedin/performance/')
          ])
          setGoogleData(google)
          setMetaData(meta)
          setLinkedinData(linkedin)
          setLastSynced(new Date().toLocaleString())
        } catch (err) {
          toast("❌ Unified sync error:", err)
        } finally {
          setIsLoading(false)
        }
      }

      runAccessAndSync()
}, [])


  if(isLoading.current){
    return(
      <div className='min-h-[100vh] flex-col flex justify-center items-center'>
        <LoadingComponent className='h-10 w-10 border-purple-500'/>
        <p className='ml-3 mt-2'>Loading...</p>
      </div>
    )
  }

  

  const chartData = [
    { day: 'Mon', spend: 400, ctr: 3.2, conversions: 12 },
    { day: 'Tue', spend: 480, ctr: 4.5, conversions: 17 },
    { day: 'Wed', spend: 300, ctr: 2.9, conversions: 9 },
    { day: 'Thu', spend: 700, ctr: 5.1, conversions: 23 },
    { day: 'Fri', spend: 620, ctr: 4.7, conversions: 21 },
    { day: 'Sat', spend: 250, ctr: 3.1, conversions: 7 },
    { day: 'Sun', spend: 500, ctr: 4.2, conversions: 15 },
  ];

  const campaigns = [
    { id: 1, platform: 'Google', name: 'Summer Sale', status: 'Active', ctr: '5.1%', cpc: '₹12.4' },
    { id: 2, platform: 'Meta', name: 'Story Ad', status: 'Paused', ctr: '3.2%', cpc: '₹8.6' },
    { id: 3, platform: 'Google', name: 'Retargeting', status: 'Active', ctr: '4.9%', cpc: '₹10.2' },
    { id: 4, platform: 'Meta', name: 'Reel Push', status: 'Active', ctr: '2.5%', cpc: '₹9.8' },
  ];

  const handleManualSync = () => {
    setLastSynced(new Date().toLocaleString());
    alert('Data refreshed (mocked).');
  };

  const filteredCampaigns = platform === 'all' ? campaigns : campaigns.filter(c => c.platform.toLowerCase() === platform);


  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 pt-20 px-6 font-sans">
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-bold text-gray-900 mb-6 text-center"
      >
        AdMentorAI - Unified Ad Intelligence
      </motion.h1>

      {/* Top Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 text-sm text-gray-600">
        <div className="space-x-2">
          <span>Platform:</span>
          <button onClick={() => setPlatform('all')} className={`px-3 py-1 rounded ${platform === 'all' ? 'bg-indigo-500 text-white' : 'bg-gray-200'}`}>All</button>
          <button onClick={() => setPlatform('google')} className={`px-3 py-1 rounded ${platform === 'google' ? 'bg-indigo-500 text-white' : 'bg-gray-200'}`}>Google</button>
          <button onClick={() => setPlatform('meta')} className={`px-3 py-1 rounded ${platform === 'meta' ? 'bg-indigo-500 text-white' : 'bg-gray-200'}`}>Meta</button>
        </div>
        <div className="mt-2 sm:mt-0">
          Last synced: <strong>{lastSynced}</strong>
          <button onClick={handleManualSync} className="ml-3 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">Refresh</button>
        </div>
      </div>

      {/* Summary Metrics with Icons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {[
          { title: 'Total Spend', value: '$3,240', icon: '💰', color: 'bg-blue-100' },
          { title: 'Impressions', value: '1.2M', icon: '📢', color: 'bg-green-100' },
          { title: 'CTR', value: '4.8%', icon: '🎯', color: 'bg-yellow-100' },
          { title: 'Conversions', value: '983', icon: '⚡', color: 'bg-purple-100' },
        ].map((card, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index, duration: 0.5 }}
            className={`p-6 rounded-xl shadow-md ${card.color} border-l-4 border-gray-300`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{card.icon}</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-700">{card.title}</h4>
                <p className="text-2xl font-bold text-gray-800">{card.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white md:p-6 rounded-xl shadow mb-12">
        <h3 className="text-xl p-3 font-semibold text-gray-800 mb-[20px]">Performance Over Time</h3>
        <ResponsiveContainer  width="100%" height={300}>
          <LineChart data={chartData} margin={{ right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#8884d8" name="Spend ($)" />
            <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="#82ca9d" name="CTR (%)" />
            <Line yAxisId="right" type="monotone" dataKey="conversions" stroke="#ffc658" name="Conversions" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-bold text-gray-900 mb-6 text-center"
      >
        AdMentorAI - Unified Ad Intelligence
      </motion.h1>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 text-sm text-gray-600">
        <div className="mt-2 sm:mt-0">
          Last synced: <strong>{lastSynced}</strong>
          <button onClick={handleManualSync} className="ml-3 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">Refresh</button>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white p-6 rounded-xl shadow mb-12">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Campaign Overview</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Platform</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Campaign</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">CTR</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">CPC</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map(campaign => (
                <tr key={campaign.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">{campaign.platform}</td>
                  <td className="px-4 py-2">{campaign.name}</td>
                  <td className="px-4 py-2">
                    <span className={`text-sm font-medium ${campaign.status === 'Active' ? 'text-green-600' : 'text-red-500'}`}>{campaign.status}</span>
                  </td>
                  <td className="px-4 py-2">{campaign.ctr}</td>
                  <td className="px-4 py-2">{campaign.cpc}</td>
                  <td className="px-4 py-2">
                    <button className="text-sm text-white px-3 py-1 rounded bg-indigo-500 hover:bg-indigo-600">
                      {campaign.status === 'Active' ? 'Pause' : 'Resume'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Suggestions Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-lg p-6 mb-12"
      >
        <h3 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center gap-2">
          🧠 Smart Suggestions
        </h3>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li><strong>Reduce bids</strong> on campaigns with <span className="text-red-600 font-semibold">high CPC</span> and <span className="text-yellow-600 font-semibold">low CTR</span></li>
          <li><strong>Boost creatives</strong> that perform well on both platforms</li>
          <li><strong>Adjust timings</strong> based on past conversion heatmaps</li>
          <li><strong>Test new call-to-actions</strong> in underperforming campaigns</li>
        </ul>
      </motion.div>
    </div>
  );
}